import { AbstractControl, FormControl } from '@angular/forms';
import {
  each,
  endsWith,
  every,
  flatMap,
  forOwn,
  get,
  has,
  compact,
  includes,
  toLower,
  isArray,
  isEmpty,
  isEqual,
  isFunction,
  isObject,
  isPlainObject,
  isString,
  keys,
  map,
  omit,
  pick,
  sample,
  set,
  some,
  sortBy,
  startsWith,
  uniq,
  size,
  replace,
  flatMapDeep,
  flow,
  groupBy,
  chain,
  slice,
  head,
  fill,
  gt,
  reduce,
  partition,
  last,
  nth,
  lt,
  eq,
  join,
  isNil,
  forEach,
  mergeWith,
  escapeRegExp,
  orderBy,
  assign,
  find,
  repeat,
} from 'lodash';

import {
  PredicateFn,
  Primitive,
} from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/common.type';
import {
  Observable,
  observableFrom,
  observableFromEvent,
  observableOf,
  rx,
  Subject,
} from '@shared/rx';
import {
  ACTION_ERROR_POSTFIX,
  ACTION_STATUS,
  ACTION_SUCCESS_POSTFIX,
  BAD_GATEWAY_STATUS_CODE,
  DEFAULT_ARTIFICIAL_INPUT_LENGTH,
  DEFAULT_ARTIFICIAL_INPUT_THRESHOLD,
  DEFAULT_NUMBER_PRECISION,
  ENTITY_ID_PATH,
  GATEWAY_TIMEOUT_STATUS_CODE,
  KEY_CODES,
  MAX_SYSTEM_FILE_NAME_LENGTH,
  MEGA_BYTE,
  TIME_FORMATS,
} from '@config/global-constants';
import { IBinaryFileType } from '@components/form/services';
import {
  Async,
  IBaseEntityType,
  IChangedFieldsDefinition,
  IModalDialogResult,
  PromiseMapOptions,
  ISelectOptionsType,
  SimpleValue,
  SyncOrAsync,
  SyncOrAsyncOrFn,
  SelectOption,
  IInventoryItem,
  ValueFormatterFn,
  IArtificialInputWatcherOption,
  EInputEvent,
  IXVIListColumn,
  MEASURE_TRANSACTION,
  IMeasure,
  DATE_FILTER_TYPES,
  DATE_PICKER_TYPES,
  ICON_TYPE,
  IOrderByUseLabelPath,
  ErrorsObject,
  ObjectId,
} from '@shared/types';

import { XVIError } from '@shared/models';
import {
  getStartOfLocalDayISOString,
  toMoment,
} from '@shared/utils/date-utils';
import {
  ITreeConverterConfig,
  ITreeData,
} from '@shared/types/common/tree.type';
import { Decimal } from 'decimal.js';
import { isDecimalNumber, DecimalValue, DecimalNumber } from '../common';
import { first as rxFirst } from 'rxjs/operators';
import { GET_NONE_VALUE_ERROR } from '@app/text-content/messages/static-error-messages';
import { CellClassParams, GridApi, RowNode } from 'ag-grid-community';
import { ScanningService } from '../services/scanning/scanning.service';
import { NumberFormatService } from '../services/number-format/number-format.service';
import { isDefaultMeasure } from './measure.utils';
import { InventoryItemVenueMeasure } from '@geniuspos/xenial-data-contracts/types/dm-entities/inventory-item-venue';
import { IDatepickerConfig } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/xux-datepicker/types';
import { XUX_DATEPICKER_PERIODS } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/constants';
import { generateTemplate } from '@text-content/messages';
import { getTheme } from '@shared/utils/theme.utils';
import { XVII18nService } from '@app/i18n/services';
import { I18n } from '@app/i18n/strings-keys';

import { environment } from '../../../environments/environment';
import { INTERNAL_GRID_ROW_ID } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component.constants';
import { ObjectId as BSONObjectId } from 'bson';

// Re-export ObjectId type from @shared/types for backward compatibility
export type { ObjectId } from '@shared/types';

export const generateObjectId = (): ObjectId =>
  new BSONObjectId().toHexString();

export const isObjectIdValid = (id: unknown): id is ObjectId => {
  if (
    typeof id !== 'string' &&
    !(id instanceof BSONObjectId) &&
    !(id instanceof Uint8Array)
  ) {
    return false;
  }

  return BSONObjectId.isValid(id);
};

export const isCloseToInteger = (value: number): boolean => {
  const zeroCount =
    Number.MAX_SAFE_INTEGER.toString().length - value.toFixed(0).length - 1;
  const multiplier = Math.pow(10, zeroCount);

  return (
    value < Number.MAX_SAFE_INTEGER &&
    Math.round(value * multiplier) === Math.round(value) * multiplier
  );
};
type DocType = IBaseEntityType;

export const getFlowMethods = (needFlowFunctions: any): any => {
  return flow(...needFlowFunctions);
};

export const removeVirtualPropertiesFromObject = (
  data: DocType,
  fieldsToSkip: string[] = [],
): DocType => {
  const clearData = cloneDataWithDepth(data, 4);
  const removeVirtualFunc = (objData: DocType): DocType => {
    Object.keys(objData).forEach((prop) => {
      if (objData.virtual) {
        delete objData.virtual;
      }
      if (
        objData[prop] &&
        isPlainObject(objData[prop]) &&
        !includes(fieldsToSkip, prop)
      ) {
        objData[prop] = removeVirtualFunc(objData[prop]);
      }

      if (
        isArray(objData[prop]) &&
        every(objData[prop], isPlainObject) &&
        !includes(fieldsToSkip, prop)
      ) {
        objData[prop] = objData[prop].map(removeVirtualFunc);
      }
    });

    return objData;
  };

  return removeVirtualFunc(clearData);
};

export const pickFieldsFromDocument = (
  doc: IBaseEntityType,
  requiredFieldsToSend: string[],
): Object => {
  return isEmpty(requiredFieldsToSend) ? doc : pick(doc, requiredFieldsToSend);
};

const removeNullPropertiesFromObjectInternal = <T extends object>(
  data: T,
  checkedObjects: Set<object>,
): T => {
  Object.keys(data).forEach((prop): void => {
    if (data[prop] === null) {
      delete data[prop];

      return;
    }

    if (!isObject(data[prop])) return;

    if (checkedObjects.has(data[prop])) return; // prevents infinite recursion
    checkedObjects.add(data[prop]);

    data[prop] = removeNullPropertiesFromObjectInternal(
      data[prop],
      checkedObjects,
    );
  });

  return data;
};

/**
 * Function for removing all null properties from object recursively
 *
 * @param data object to be edited
 * @returns result object
 */
export const removeNullPropertiesFromObject = <T extends object>(
  data: T,
  checkedObjects: Set<object> = new Set([data]),
): T => {
  return isObject(data)
    ? removeNullPropertiesFromObjectInternal(data, checkedObjects)
    : data;
};

/**
 * Function for converting 'true' or 'false' to boolean values
 *
 * @param val - value to be converted
 * @returns
 */
export const getBool = (val: SimpleValue): boolean => {
  if (!val) return false;
  const num = +val;

  return !isNaN(num)
    ? !!num
    : !!String(val).toLowerCase().replace(String(!!0), '');
};

export const entityListToSelectOptions = (
  inputList: any[],
  itemSourceIdKeyPath?: string,
): ISelectOptionsType[] => {
  if (!Array.isArray(inputList)) {
    return [];
  }

  if (isString(sample(inputList))) {
    return inputList.map((item) => ({
      text: item,
      value: item,
    }));
  }

  return inputList.map((item) => ({
    text: item['name'],
    value: item[itemSourceIdKeyPath || ENTITY_ID_PATH],
  }));
};

/* eslint-disable */

// create and returns query filter to fetch items with entity_id from provided list
export const queryWithEntityIds = (
  idList: string[],
  entityIdPath: string = ENTITY_ID_PATH,
) => {
  const stringifiedIds = uniq(idList).join();

  return { filter: `${entityIdPath} eq in(${stringifiedIds})` };
};

// create and returns query filter to fetch items without entity_id from provided list
export const queryWithoutEntityIds = (idList: string[]) => {
  const stringifiedIds = uniq(idList).join();

  return { filter: `entity_id eq nin(${stringifiedIds})` };
};

export const generateId = () =>
  Date.now().toString(16) + Math.random().toString(16).substr(3);

export const defaultOptionMapper = (
  rawOptions: any[],
  itemSourceIdKeyPath = ENTITY_ID_PATH,
) => {
  const options = Array.isArray(rawOptions) ? rawOptions.filter(Boolean) : [];
  if (!Array.isArray(options) || isEmpty(options)) {
    return [];
  }
  // if sample option item does not contain value and text fields,
  // then map values with default entity mapper
  if (has(sample(options), 'value') && has(sample(options), 'text')) {
    return options;
  }

  return entityListToSelectOptions(options, itemSourceIdKeyPath);
};

export const deleteIdsRecursively = (data, isIdNeeded: boolean = false) => {
  forOwn(data, (val, key) => {
    if (key === 'mappings') {
      data[key] = val.map((mapping) =>
        omit(mapping, [
          'document_id',
          '_id',
          ENTITY_ID_PATH,
          'created_at',
          'created_by',
          'updated_at',
          'updated_by',
        ]),
      );
    } else if (isArray(val)) {
      val.forEach((el) => deleteIdsRecursively(el));
    } else if (isObject(val)) {
      deleteIdsRecursively(val);
    } else if ((key === '_id' && !isIdNeeded) || key === ENTITY_ID_PATH) {
      delete data[key];
    }
  });

  return data;
};

export const isObservable = <T>(
  subject: any | Observable<T>,
): subject is Observable<T> => {
  return hasMethod(subject, 'subscribe');
};

export const isSubject = <T>(
  subject: any | Subject<T>,
): subject is Subject<T> => {
  try {
    const hasBasicMethods =
      hasMethod(subject, 'subscribe') &&
      hasMethod(subject, 'next') &&
      hasMethod(subject, 'complete') &&
      hasMethod(subject, 'error');

    if (!hasBasicMethods) {
      return false;
    }

    const hasSubjectFeatures =
      hasMethod(subject, 'asObservable') &&
      hasMethod(subject, 'pipe') &&
      'closed' in subject &&
      'isStopped' in subject &&
      'hasError' in subject;

    const isNotPlainObservable = hasMethod(subject, 'next');

    return !!(hasBasicMethods && hasSubjectFeatures && isNotPlainObservable);
  } catch {
    return false;
  }
};

export const isPromise = <T>(
  subject: any | Promise<T>,
): subject is Promise<T> => {
  return hasMethod(subject, 'then');
};

export const isAsync = <T>(value: SyncOrAsync<T>): value is Async<T> =>
  isObservable(value) || isPromise(value);

export const hasMethod = (
  subject: any,
  methodName: string | string[],
): boolean => {
  return isFunction(get(subject, methodName));
};

const functionToObservable = <T>(factory: () => T): Observable<T> => {
  return new Observable((observer) => {
    try {
      observer.next(factory());
      observer.complete();
    } catch (error) {
      observer.error(error);
    }
  });
};

export const asObservable = <T>(src: SyncOrAsyncOrFn<T>): Observable<T> => {
  if (isPromise(src)) return observableFrom(src);
  if (isFunction(src)) return functionToObservable(src);

  return isObservable(src) ? src : observableOf(src);
};

export const patchEffectiveDate = (document) => {
  each(keys(document), (key: string) => {
    if (isPlainObject(document[key])) return patchEffectiveDate(document[key]);
    if (isArray(document[key]))
      return map(document[key], (doc) => patchEffectiveDate(doc));
    if (
      key === 'effective_date' &&
      document[key] === getStartOfLocalDayISOString()
    )
      set(document, '_is_current', true);
  });

  return document;
};

/**
 * controlValueObs
 * Helper that SAFELY returns an observable for passed control values
 * STARTING from the current value of the control.
 * Default value can be passed for cases when control is undefined
 *
 * TODO: Remove this function and replace usage with {@link onControlValueChanged}
 * @deprecated Use {@link onControlValueChanged}
 */
export const controlValueObs = (
  control: AbstractControl,
  defaultValue = null,
): Observable<any> => {
  if (!get(control, 'valueChanges')) {
    return observableOf(defaultValue);
  }

  return control.valueChanges.pipe(rx.startWith(control.value));
};

/**
 * Replacing one single quote with two ones to fit the OData rules
 * @param {string} filterString - Filter string
 * @param {boolean} replaceBracketsWithUnicodes - Replace brackets
 * @returns {string} - Filtered string
 */
export const prepareOdataString = (
  filterString: string,
  replaceBracketsWithUnicodes: boolean = false,
): string => {
  if (replaceBracketsWithUnicodes) {
    return filterString
      .replace(/'/gi, "''")
      .replace(/\(/g, 'u28')
      .replace(/\)/g, 'u29');
  }

  return filterString.replace(/'/gi, "''");
};

// prettier-ignore
export const encodeFilterValue = (str: string): string => {
  const codes = {
    '-': '%2D',
    '_': '%5F',
    '.': '%2E',
    '!': '%21',
    '~': '%7E',
    '*': '%2A',
    "\'": '%27%27',
    '(': '%28',
    ')': '%29',
    '/': '%2F',
  };

  const charsToReplace = Object.keys(codes).join('');
  const regex = new RegExp(`[${escapeRegExp(charsToReplace)}]`, 'g');
  return encodeURIComponent(str).replace(regex, (char) => codes[char]);
};

export const getDataByReference = (schema, reference) => {
  const handleRef = reference.replace(/^#\//, '').replace(/\//g, '.');
  let schemaRefPath = `${schema.definition ? 'definition' : ''}.${handleRef}`;

  if (startsWith(schemaRefPath, '.')) {
    schemaRefPath = schemaRefPath.replace(/^./, '');
  }

  return get(schema, schemaRefPath, null);
};

/**
 * addTrailingZeros function for displaying numbers with a calculated precision
 * @param value - number for rounding
 * @param step - number equal Math.pow(10, -precision).
 * @returns - number as a string.
 * e.g if the step is 0.001 and the value is 3.1, the result is '3.100'
 */
export const addTrailingZeros = (value: number, step: number): string => {
  const fractionPart = String(step).split('.')[1];

  const decimals = fractionPart ? fractionPart.length : 0;

  return value.toFixed(decimals);
};

export const confirmed = (modalResult: IModalDialogResult): boolean => {
  return !!get(modalResult, 'confirmed');
};

/**
 * Binary checks file type from fileDescriptor
 * @param {File} fileDescriptor - image or file information
 * @returns {Promise<IBinaryFileType>} - upload with file byte type, name, initial mime type and hex-info
 */
const getBinaryFileType = (fileDescriptor: File): Promise<IBinaryFileType> => {
  return new Promise((resolve): void => {
    let upload: IBinaryFileType = <IBinaryFileType>{};

    if (fileDescriptor) {
      const filereader = new FileReader();

      filereader.onloadend = (evt: any): void => {
        if (evt.target.readyState === FileReader['DONE']) {
          const uint = new Uint8Array(evt.target.result);
          const bytes = [];
          uint.forEach((byte) => {
            bytes.push(byte.toString(16));
          });
          const hex = bytes.join('').toUpperCase();

          upload = {
            hex,
            filename: fileDescriptor.name,
            filetype: fileDescriptor.type
              ? fileDescriptor.type
              : 'Unknown/Extension missing',
            binaryFileType: getMimetype(hex),
          };
        }

        resolve(upload);
      };

      const blob = fileDescriptor.slice(0, 4);
      filereader.readAsArrayBuffer(blob);
    } else {
      upload = {
        filename: '',
        filetype: 'Unknown/Extension missing',
        binaryFileType: 'Unknown filetype',
        hex: '',
      };

      resolve(upload);
    }
  });
};

/**
 * Returns image type by hex data (first 4 bytes)
 * @param {string} signature - hex definition of checked file
 * @returns {string} - file type definition
 */
const getMimetype = (signature: string): string => {
  return 'Unknown filetype';
};

export const fileCheckFunctions = {
  getBinaryFileType,
  getMimetype,
};

export const arrayHasEmptyValues = (array: string[]): boolean =>
  some(array, (item) => item === '');

export const isAbsoluteUrl = (
  url: string,
  httpOnly: boolean = true,
): boolean => {
  const regEx = httpOnly
    ? /^(?:https?:)?\/\//
    : /^(?:[a-zA-Z][a-zA-Z\d+.-]*:)?\/\//;

  return regEx.test(url);
};

export const extendActionWithPostfix = (actionTypes: string[]): string[] => {
  return flatMap(actionTypes, (action) => {
    return [
      action,
      `${action}${ACTION_ERROR_POSTFIX}`,
      `${action}${ACTION_SUCCESS_POSTFIX}`,
    ];
  });
};

export const isActionPostfixError = (actionType: string): boolean => {
  return endsWith(actionType, ACTION_ERROR_POSTFIX);
};

export const isActionPostfixSuccess = (actionType: string): boolean => {
  return endsWith(actionType, ACTION_SUCCESS_POSTFIX);
};

export const isActionStatusSuccess = (actionStatusItem): boolean => {
  return get(actionStatusItem, 'status') === ACTION_STATUS.SUCCESS;
};

export const isActionStatusError = (actionStatusItem): boolean => {
  return get(actionStatusItem, 'status') === ACTION_STATUS.ERROR;
};

export const notUndefinedFilter = (value): boolean => {
  return value !== undefined;
};

export const trackByValue = <T = string>(index, item: { value: T }): T => {
  return item.value;
};

export const isTimeoutStatusCode = (error: XVIError): boolean => {
  const timeoutErrors = [BAD_GATEWAY_STATUS_CODE, GATEWAY_TIMEOUT_STATUS_CODE];
  const statusCode = get(error, 'status', null);

  return timeoutErrors.some((code) => code === statusCode);
};

/*
 * Helper for limit threads
 *
 * Example of usage in basic case
 *
 * const docs = [{ name: 'test 1', entity_id: 'test-id-01' }, { name: 'test 2', entity_id: 'test-id-02' }]
 * promiseMap(
 *      docs,
 *      (doc) => {
 *         return doc.name = 'ne name';
 *      },
 *   );
 *
 * Example of usage with async operation:
 *   const docs = [{ name: 'test 1', entity_id: 'test-id-01' }, { name: 'test 2', entity_id: 'test-id-02' }]
 *   await promiseMap(
 *      docs,
 *      async (doc) => {
 *         const entityId = doc.entity_id;
 *         // do some async operation
 *      },
 *   );
 *
 * Also we can change concurrency for that we should send with options
 *
 * promiseMap(iterable, mapperFn, { concurrency: 30 })
 *
 */
export const promiseMap = async (
  iterable,
  mapper: Function,
  options?: PromiseMapOptions,
): Promise<any> => {
  const defaultOptions = {
    concurrency: 20,
  };
  const results = [];
  const iterator = iterable[Symbol.iterator]();
  let index = 0;
  const wrappedMapper: Function = (): Promise<any> => {
    const next = iterator.next();
    if (next.done) return null;

    // eslint-disable-next-line
    const i = index++;
    const mapped = mapper(next.value, i);

    return Promise.resolve(mapped).then((resolved) => {
      results[i] = resolved;

      return wrappedMapper();
    });
  };

  const promises: Promise<any>[] = [];
  let { concurrency } = Object.assign({}, defaultOptions, options);
  // eslint-disable-next-line
  while (concurrency-- > 0) {
    const promise = wrappedMapper();
    if (!promise) break;

    promises.push(promise);
  }

  await Promise.all(promises);

  return results;
};

/**
 * The method for custom cloning data to specific deep level
 */
export const cloneDataWithDepth = (
  data: any,
  depthInitial: number = 5,
): any => {
  let depth: number = depthInitial;
  if (!isObject(data) || isFunction(data) || !depth || depth < 0) return data;

  if (depth === 1) {
    return isArray(data) ? [...data] : { ...data };
  }

  depth = isArray(data) ? depth : depth - 1;

  const cloneFunc = (level: number, obj: any): any => {
    if (!isObject(obj) || isFunction(obj)) return obj;

    const nextLevel = level - 1;
    const nextMethodCall = nextLevel ? cloneFunc.bind(null, nextLevel) : null;

    return isArray(obj)
      ? arrayCloningHandler(obj, nextMethodCall)
      : objectCloningHandler(obj, nextMethodCall);
  };

  return cloneFunc(depth, data);
};

const arrayCloningHandler = (data: any, subFunc: Function): any[] => {
  const newArray = [...data];

  return map(newArray, (item) => (subFunc ? subFunc(item) : item));
};

const objectCloningHandler = (data: any, subFunc?: Function): any => {
  const newObj = { ...data };

  for (const key in newObj) {
    if (
      !newObj.hasOwnProperty(key) ||
      isDecimalNumber(newObj[key]) || // should think decimal number as pr obj
      !isObject(newObj[key]) ||
      isFunction(newObj[key])
    ) {
      continue;
    }
    if (isArray(newObj[key])) {
      newObj[key] = subFunc
        ? map(newObj[key], (item) => subFunc(item))
        : [...newObj[key]];
    } else {
      newObj[key] = subFunc ? subFunc({ ...newObj[key] }) : { ...newObj[key] };
    }
  }

  return newObj;
};

export const getColumnCaptionPath = (field: string = null): string => {
  return field ? `${field}_caption_path` : null;
};

export const omitFileNameSpecialCharacters = (name: string = ''): string => {
  return name
    .replace(/\&/g, '')
    .replace(/ /g, '_')
    .replace(/</g, '')
    .replace(/>/g, '')
    .replace(/\"/g, '')
    .replace(/\'/g, '');
};

const simpleEmptyPlaceholderValue = Symbol('empty-val-placeholder');

export const simpleEmptyPlaceholder = (): Symbol => {
  return simpleEmptyPlaceholderValue;
};

export const replaceEmptyValuePlaceholderWithNull = <T>(value: T): T | null => {
  // @ts-ignore
  if (value === simpleEmptyPlaceholderValue) {
    return null;
  }

  return value;
};

export const isArrayElementsEquals = (first: any[], second: any): boolean => {
  if (size(first) !== size(second)) {
    return false;
  }

  return isEqual(sortBy(first), sortBy(second));
};

export const isArrayElementsOrScalarsEquals = (
  first: any,
  second: any,
): boolean => {
  if (isArray(first) && isArray(second))
    return isArrayElementsEquals(first, second);

  return isEqual(first, second);
};

interface IArraysOfScalarsComparator {
  next: (items: Primitive[]) => void;
  getResult: () => ArraysOfScalarsCompareResult;
  hasResult: () => boolean;
}

type ArraysOfScalarsCompareResult = Primitive[];

export const formatBytes = (bytes: number): number => {
  if (!Number.isInteger(bytes)) {
    return 0;
  }

  return Math.round(bytes / MEGA_BYTE);
};
export const getArraysOfScalarsComparator = (): IArraysOfScalarsComparator => {
  let hasResult = false;
  const previousPlaceholder = Symbol('previous placeholder');
  let previous: any = previousPlaceholder;
  let result: ArraysOfScalarsCompareResult = [];

  return {
    next: (items): void => {
      if (hasResult) return;

      const sortedItems = sortBy(items);
      if (
        !isEqual(previous, previousPlaceholder) &&
        !isEqual(sortedItems, previous)
      ) {
        result = ['Varies'];
        hasResult = true;

        return;
      }

      if (isEmpty(sortedItems)) {
        result = [];
        previous = sortedItems;

        return;
      }

      result = items;
      previous = sortedItems;
    },
    getResult: (): ArraysOfScalarsCompareResult => {
      return result;
    },
    hasResult: (): boolean => {
      return hasResult;
    },
  };
};

/**
 * This method removes FormControls from data to avoid getting them into a cloning
 * and freeze the cloning items process
 * Note: this method can find a FormControl only to the second deep object level,
 * if you need to prepare a FormControl on the first deep object level,
 * you need to set up the 'repeat' parameter as false
 */
export const prepareControlsInsideDataForCloning = (
  obj: IChangedFieldsDefinition | IChangedFieldsDefinition[],
  repeat: boolean = true,
): IChangedFieldsDefinition | IChangedFieldsDefinition[] => {
  if (!isObject(obj)) return obj;

  const newObj = isArray(obj) ? [...obj] : { ...obj };

  for (const key in newObj) {
    if (
      !newObj.hasOwnProperty(key) ||
      !isObject(newObj[key]) ||
      isFunction(newObj[key]) ||
      isArray(newObj[key])
    ) {
      continue;
    }

    if (
      newObj[key] instanceof FormControl ||
      newObj[key] instanceof AbstractControl
    ) {
      const omitFields = ['parent', 'root', '_parent'];
      set(newObj, key, omit(newObj[key], omitFields));
    } else if (repeat) {
      newObj[key] = prepareControlsInsideDataForCloning(newObj[key], false);
    }
  }

  return newObj;
};

export const isTextPresentInSelectOptions = (
  options: SelectOption[],
  text: string,
): boolean => {
  if (isEmpty(options)) return false;

  const textOptions = compact(map(options, (option) => toLower(option.text)));
  const searchValue = toLower(text).trim();
  const isPresent = isEmpty(searchValue) || includes(textOptions, searchValue);

  return isPresent;
};

export const asSelectOptions = (
  input,
  itemSourceIdKeyPath: string = ENTITY_ID_PATH,
): Observable<ISelectOptionsType[]> => {
  return asObservable(input).pipe(
    rx.map((items) => defaultOptionMapper(items, itemSourceIdKeyPath)),
  );
};

export const removeActionPostfix = (actionType: string): string => {
  if (isActionPostfixError(actionType))
    return replace(actionType, ACTION_ERROR_POSTFIX, '');
  if (isActionPostfixSuccess(actionType))
    return replace(actionType, ACTION_SUCCESS_POSTFIX, '');

  return actionType;
};

export const flatInventoryItemVenueData: <T>(arg: ITreeData[]) => T[] = (
  treeData,
) => {
  if (isEmpty(treeData)) return [];

  return flatMapDeep(
    map(treeData, (majorList) =>
      map(majorList.children, (minorList) => minorList.children),
    ),
  );
};

export const flatTreeDataByHasChildren = <T>(
  data: ITreeData[] | T[],
  options?: {
    selectFields?: string[];
  },
): T[] => {
  if (isEmpty(data)) return [];

  const { selectFields = [] } = options || {};

  if (isTreeDataStructure<T>(data)) {
    return chain(data)
      .map((item: ITreeData) =>
        flatTreeDataByHasChildren(item.children as ITreeData[], options),
      )
      .flatMapDeep()
      .value() as unknown as T[];
  }

  return isEmpty(selectFields)
    ? (data as T[])
    : map(data as T[], (item) => pick(item, selectFields) as T);
};

export const isTreeDataStructure = <T>(data: ITreeData[] | T[]): boolean => {
  if (isEmpty(data)) return false;

  return has(head(data), 'children');
};

export const accuratePrecision = (
  num: number,
  precision = DEFAULT_NUMBER_PRECISION,
): string => {
  return new Decimal(num).toFixed(precision);
};

export const dataURLtoFile = (dataUrl, fileName): File => {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  // eslint-disable-next-line
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], fileName, { type: mime });
};

export const truncateDecimals = (
  num: DecimalValue,
  decimalPlaces: number,
): number => {
  const factor = new DecimalNumber(10).pow(
    DecimalNumber.buildDecimalValue(decimalPlaces, 0),
  );
  const truncatedNum = factor
    .mul(DecimalNumber.buildDecimalValue(num, 0))
    .floor()
    .div(factor);

  return parseFloat(truncatedNum.toFixed(decimalPlaces));
};

/**
 * Group by major category name and minor category name
 * Sorted grouped result
 * @param inventoryItems
 * @returns grouped and sorted object
 */
export const groupByMajorMinorCategoryAndSort = (
  inventoryItems: IInventoryItem[],
): Record<string, IInventoryItem[]> => {
  let groupedItems = groupBy(inventoryItems, (inventoryItem) => {
    return `${inventoryItem.major_category_name ?? inventoryItem?.majorCategory}${
      inventoryItem.minor_category_name ?? inventoryItem?.minorCategory
    }`;
  });

  // Sort items case insensitive
  groupedItems = chain(groupedItems)
    .toPairs()
    .sortBy(([name]) => name?.toLowerCase())
    .fromPairs()
    .value();

  return groupedItems;
};

/**
 * Group by gl account name
 * Sorted grouped result
 * @param inventoryItems
 * @returns grouped and sorted object
 */
export const groupByGlAccountAndSort = (
  inventoryItems: IInventoryItem[],
): object => {
  let groupedItems = groupBy(inventoryItems, (inventoryItem) => {
    return `${inventoryItem?.gl_account_name.toLowerCase()}`;
  });
  // Sort items
  groupedItems = chain(groupedItems).toPairs().sortBy(0).fromPairs().value();

  return groupedItems;
};

/**
 * Generate header with spaces
 * @param name
 * @param suffix
 * @returns ‘name (suffix)’
 */
export const generateSpacesHeader = (name: string, suffix: string): string => {
  return name && suffix ? `${name} (${suffix})` : '';
};

/**
 * Generate unit text without spaces
 * @param description
 * @param factorValue
 * @returns description[factorValue]’
 */
export const generateUnitText = (
  description: string,
  factorValue: string | number,
  numberFormatService: NumberFormatService,
): string => {
  return `${description}[${numberFormatService.getNumberFormatter(false)({ value: factorValue })}]`;
};

export const generateUnitTextV2 = (
  measure: InventoryItemVenueMeasure | Partial<IMeasure>,
  options: {
    numberFormatService: NumberFormatService;
    measureTransactionType?: MEASURE_TRANSACTION;
    notNeedAddSign?: boolean;
  },
): string => {
  const {
    numberFormatService,
    measureTransactionType,
    notNeedAddSign = false,
  } = options;

  const addDefaultSign =
    !notNeedAddSign && isDefaultMeasure(measure, measureTransactionType);

  return `${addDefaultSign ? '*' : ''}${measure.description}[${numberFormatService.getNumberFormatter(false)({ value: measure.factor_value })}]`;
};

/**
 * Generate text with spaces
 * @param name
 * @param suffix
 * @returns ‘name [suffix]’
 */
export const generateSpacesText = (name: string, suffix: string): string => {
  return name && suffix ? `${name} [${suffix}]` : '';
};

/**
 * Generate text Truncation with Ellipsis use W to calculate
 * @param text
 * @param maxWidthCharacterCount allowed W characters count
 * @param fontSize the text fontSize X pt
 * @returns text...’
 */
export const truncationText = (
  text: string,
  maxWidthCharacterCount: number,
  fontSize: number,
): string => {
  if (isEmpty(text)) {
    return '';
  }
  const maxWidth = getTextWidth(
    fill(Array(maxWidthCharacterCount), 'W').join(''),
    fontSize,
  );
  let str = '';
  let haveEllipsis = false;
  for (let i = 0; i < text.length; i += 1) {
    str += text.charAt(i);
    if (getTextWidth(str, fontSize) > maxWidth) {
      haveEllipsis = true;
      break;
    }
  }

  return `${str}${haveEllipsis ? '...' : ''}`;
};

export const getTextWidth = (text: string, fontSize: number): number => {
  const _span = document.createElement('span');
  _span.innerText = text;
  _span.style.fontSize = `${fontSize}pt`;
  _span.style.position = 'absolute';
  _span.style.visibility = 'hidden';
  document.body.appendChild(_span);
  const width = _span.offsetWidth;
  document.body.removeChild(_span);

  return width;
};

export const getObservableValue = <T>(observable: Observable<T>): T => {
  let result: T;
  let hasValue = false;

  const subscription = observable.pipe(rxFirst()).subscribe((value: T) => {
    result = value;
    hasValue = true;
  });

  subscription.unsubscribe();

  if (!hasValue) {
    throw new Error(GET_NONE_VALUE_ERROR);
  }

  return result;
};

/**
 *
 * @param fileNamePrefix
 * @param fileNameTruncationText this is use to truncation for long file name
 * @param fileNameSuffix
 * @returns get single export file name `${fileNamePrefix} - ${fileNameTruncationText} - ${fileNameSuffix}.pdf`
 */
export const getSingleExportFileName = (
  fileNamePrefix: string,
  fileNameTruncationText: string,
  fileNameSuffix?: string,
): string => {
  let fileNameTruncationTextTemplate = fileNameTruncationText;
  const dateTextTemplate = fileNameSuffix
    ? toMoment(fileNameSuffix).format(TIME_FORMATS.L)
    : fileNameSuffix;
  // Handle special characters
  const reg = /^\.|[\\\\/:.*?\"<>|]/g;
  if (reg.test(fileNameTruncationTextTemplate)) {
    fileNameTruncationTextTemplate = replace(
      fileNameTruncationTextTemplate,
      reg,
      '',
    );
  }
  // Handles file name length `${fileNamePrefix} - ${fileNameTruncationText} - ${fileNameSuffix}.pdf`
  if (
    fileNamePrefix &&
    fileNameTruncationTextTemplate &&
    dateTextTemplate &&
    10 +
      fileNamePrefix.length +
      fileNameTruncationTextTemplate.length +
      dateTextTemplate.length >
      MAX_SYSTEM_FILE_NAME_LENGTH
  ) {
    fileNameTruncationTextTemplate = slice(
      fileNameTruncationTextTemplate,
      0,
      MAX_SYSTEM_FILE_NAME_LENGTH -
        10 -
        fileNamePrefix.length -
        dateTextTemplate.length,
    ).join('');
  }

  if (fileNamePrefix && fileNameTruncationTextTemplate && !fileNameSuffix) {
    // the number 7 is the joint mark( - ).length and suffix .pdf length; 3 + 4;
    if (
      gt(
        7 + fileNamePrefix.length + fileNameTruncationTextTemplate.length,
        MAX_SYSTEM_FILE_NAME_LENGTH,
      )
    ) {
      fileNameTruncationTextTemplate = slice(
        fileNameTruncationTextTemplate,
        0,
        MAX_SYSTEM_FILE_NAME_LENGTH - 7 - fileNamePrefix.length,
      ).join('');
    }

    return `${fileNamePrefix} - ${fileNameTruncationTextTemplate}`;
  }

  return `${fileNamePrefix} - ${fileNameTruncationTextTemplate} - ${dateTextTemplate}`;
};

export const partitionAndSortItemsByChargeable = <T extends IBaseEntityType>(
  items: T[],
  chargeableFieldPath: string = 'chargeable',
): T[] => {
  return reduce(
    partition(items, chargeableFieldPath),
    (sortedItems, partitionItems) => {
      return [
        ...sortedItems,
        ...sortBy(partitionItems, (item) => toLower(item.name)),
      ];
    },
    [],
  );
};

export const isNotClickGridCell = (event: MouseEvent): boolean => {
  return (
    !(event.target as Element)?.closest('.ag-cell') &&
    !(event.target as Element)?.classList?.contains('ag-cell')
  );
};

export const getSelectedItemsDescriptionText = (
  selectedCount: number,
  total: number,
  options: {
    i18nService: XVII18nService;
    entities?: string;
  },
) => {
  const { i18nService, entities = I18n.Forms.GridMessages.Items } = options;

  if (isEqual(selectedCount, 0)) {
    return i18nService.instant(I18n.Forms.GridMessages.ShowingEntities, {
      count: total,
      entities: i18nService.instant(entities),
    });
  }

  return i18nService.instant(
    I18n.Forms.GridMessages.ShowingEntitiesWithSelected,
    {
      count: total,
      entities: i18nService.instant(entities),
      selectedCount: selectedCount,
    },
  );
};

export const getFormBtnHidden = <T = any>(
  baseHidden: boolean | PredicateFn<boolean>,
  formValue?: T,
) => {
  return isFunction(baseHidden) ? baseHidden(formValue) : baseHidden;
};

export const getBaseMeasure = (
  baseMeasure: {
    description: string;
    factor_value: number | string;
  },
  numberFormatFn: ValueFormatterFn,
  i18nService: XVII18nService,
): string => {
  return isEmpty(baseMeasure?.description)
    ? `${i18nService.translate(I18n.Common.Each)}[1]`
    : `${get(baseMeasure, 'description')}[${numberFormatFn({
        value: get(baseMeasure, 'factor_value'),
      })}]`;
};

export const judgeScanned = (keyTimes: number[]): boolean => {
  if (isEmpty(keyTimes) || lt(size(keyTimes), 1)) {
    return false;
  }

  const isScan: boolean =
    last(keyTimes) - nth(keyTimes, -2) < DEFAULT_ARTIFICIAL_INPUT_THRESHOLD;
  return isScan;
};

export const numberCheckValid = (key: string): boolean => {
  return /^[-|\d|\.]$/.test(key);
};

export const artificialInputWatcher = (
  inputEventName: EInputEvent,
  artificialInputWatcherOption: IArtificialInputWatcherOption,
  callBack?: Function,
  inputCheckValid?: (key: any) => boolean,
): void => {
  const {
    eventHandler,
    passByKeyList,
    nativeElement,
    scanningService,
    artificialInputThreshold,
    cancelSubscription$,
    updateValueBeforeEventHandler,
  } = artificialInputWatcherOption;

  const inputEvent$ = observableFromEvent<KeyboardEvent>(
    nativeElement,
    inputEventName,
  );
  let count: number = 0;

  inputEvent$
    .pipe(
      rx.filter((event: KeyboardEvent) => {
        const eventKey = event.key;
        // Allow all key combinations‌（Ctrl/Alt/Meta + other key）
        if (event.ctrlKey || event.altKey || event.metaKey) {
          return false;
        }
        // just call handler when is not number and not in pass by key list
        const shouldHandle =
          !includes(passByKeyList, eventKey) && !eq(size(eventKey), 1);
        if (shouldHandle) {
          // For Chinese input method to type characters, we use backspace key in keyboard
          if (eq(event?.key, KEY_CODES.BACK_SPACE)) {
            const curValue = (nativeElement as any).value;
            artificialInputWatcherOption.isValidScanning = false;
            callBack(null, artificialInputWatcherOption, curValue);
          }
          eventHandler?.(event, true);

          return false;
        }
        count++;
        event.preventDefault();

        return true;
      }),
      rx.buffer(inputEvent$.debounceTime(artificialInputThreshold)),
      rx.filter((events) => {
        if (isEmpty(events)) {
          return false;
        }
        const isArtificialInput = lt(count, DEFAULT_ARTIFICIAL_INPUT_LENGTH);
        count = 0;

        // we define it as scanning
        if (!isArtificialInput) {
          // only need keydown to scanning
          if (eq(inputEventName, EInputEvent.KEYDOWN)) {
            const curValue = (nativeElement as any).value;
            const firstItemIndex = scanningService.getFirstItemIndex(
              events,
              scanningService.scanOptions,
              false,
            );
            // if exist index, should  call callback to sync value, especially for dropdown menu.
            if (!eq(firstItemIndex, -1)) {
              callBack(
                null,
                { ...artificialInputWatcherOption, isValidScanning: true },
                curValue,
              );
            }
            scanningService.scanningResult(events, scanningService.scanOptions);
          }

          return false;
        }

        return true;
      }),
      rx.takeUntil(cancelSubscription$),
    )
    .subscribe((events: KeyboardEvent[]) => {
      artificialInputWatcherOption.isValidScanning = false;
      if (isFunction(callBack)) {
        let newValue: string = null;
        forEach(events, (event) => {
          if (
            inputCheckValid && isFunction(inputCheckValid)
              ? updateValueBeforeEventHandler && inputCheckValid(event.key)
              : updateValueBeforeEventHandler
          ) {
            const curValue = (nativeElement as any).value;
            const curValueLength = size(curValue);
            const eventKey = event.key;
            const selectionStart = (nativeElement as any).selectionStart;
            const selectionEnd = (nativeElement as any).selectionEnd;
            newValue = `${curValue.slice(0, selectionStart)}${eventKey}${curValue.slice(selectionEnd, curValueLength)}`;
            (nativeElement as any).value = newValue;
            const newCursorPosition = selectionStart + size(eventKey);
            (nativeElement as any).setSelectionRange(
              newCursorPosition,
              newCursorPosition,
            );
          }
          eventHandler?.(event, true);
          callBack(event, artificialInputWatcherOption, newValue);
        });
      }
    });
};

export const getExtraQuickFilterText = ({
  name,
  extraValues,
}: {
  name: string;
  extraValues: string[];
}): string => {
  let quickFilterText = '';

  if (!isEmpty(name)) {
    quickFilterText = `${name} `;
  }

  if (gt(size(extraValues), 0)) {
    quickFilterText += join(extraValues, ' ');
  }

  return quickFilterText;
};

export const getScanningHighlightRowClass = (
  scanningService: ScanningService,
): {
  [cssClassName: string]: ((params: any) => boolean) | string;
} => {
  return {
    'scanning-highlight': (params: CellClassParams): boolean => {
      const rowData = get(params, 'data');

      return (
        !isNil(scanningService.scannedItemId) &&
        includes(
          [get(rowData, ENTITY_ID_PATH), get(rowData, 'item_entity_id')],
          scanningService.scannedItemId,
        )
      );
    },
  };
};

export const getQuickFilterConfigForScanning = (
  isTreeView: boolean,
  quickFilterProperty: string = 'name',
): Partial<IXVIListColumn> => {
  return isTreeView
    ? {
        getQuickFilterTextForTreeView: (params: IBaseEntityType): string =>
          getExtraQuickFilterText({
            name: get(params, quickFilterProperty),
            extraValues: get(params, 'upc_codes', []),
          }),
      }
    : {
        getQuickFilterText: (params: IBaseEntityType): string =>
          getExtraQuickFilterText({
            name: get(params, 'value'),
            extraValues: get(params, 'data.upc_codes', []),
          }),
      };
};

// For one available option, reduce config form rules
export const getDatePickerDefaultConfig = (
  type: DATE_PICKER_TYPES,
): IDatepickerConfig & { overrideDatePickerType: string } => {
  switch (type) {
    case DATE_FILTER_TYPES.SINGLE:
      return {
        allowEdit: true,
        show2ndField: false,
        autoCorrectEndDate: false,
        required: true,
        mergeDateFields: true,
        placeholder: toMoment().localeData().longDateFormat(TIME_FORMATS.L),
        overrideDatePickerType: XUX_DATEPICKER_PERIODS.SINGLE.type,
      };

    default:
      return {
        overrideDatePickerType: XUX_DATEPICKER_PERIODS.SINGLE.type,
      };
  }
};

export const getIcon = (
  type: ICON_TYPE,
  options?: {
    typeAsImageName?: boolean;
  },
): string => {
  switch (type) {
    default:
      return '';
    case ICON_TYPE.DOUBLE_LEFT:
    case ICON_TYPE.DOUBLE_ARROW_LEFT:
      return generateTemplate('assets/img/{{{brandName}}}-{{{imgName}}}.svg', {
        brandName: getTheme(),
        imgName: options?.typeAsImageName ? type : 'double-left',
      });
    case ICON_TYPE.DOUBLE_RIGHT:
    case ICON_TYPE.DOUBLE_ARROW_RIGHT:
      return generateTemplate('assets/img/{{{brandName}}}-{{{imgName}}}.svg', {
        brandName: getTheme(),
        imgName: options?.typeAsImageName ? type : 'double-right',
      });
  }
};

export const getExpandableIconType = (
  collapse: boolean,
  { config: imageConfig },
) => {
  if (imageConfig.RTL) {
    return collapse
      ? ICON_TYPE.DOUBLE_ARROW_LEFT
      : ICON_TYPE.DOUBLE_ARROW_RIGHT;
  }

  return collapse ? ICON_TYPE.DOUBLE_ARROW_RIGHT : ICON_TYPE.DOUBLE_ARROW_LEFT;
};

export const mergeWhenNil = <T extends object>(
  original: T,
  override: Partial<T>,
): T =>
  mergeWith(original, override, (objValue, srcValue) =>
    isNil(objValue) ? srcValue : objValue,
  );

export const getLeafPropCollection = (treeNodes, targetProp) => {
  const result = []; // Store the final property collection

  // Traverse nodes at the current level (use _.forEach to simplify traversal)
  forEach(treeNodes, (node) => {
    // Check if it's a leaf node: children does not exist or is empty (use _.isEmpty to handle various empty cases)
    const isLeaf = isEmpty(get(node, 'children'));

    if (isLeaf) {
      // For leaf nodes: safely get the target property, add to result if it exists
      const propValue = get(node, targetProp);
      if (propValue) {
        // Optional: filter out empty property values
        result.push(propValue);
      }
    } else {
      // For non-leaf nodes: recursively process child nodes and merge results
      const childLeafProps = getLeafPropCollection(
        get(node, 'children'),
        targetProp,
      );
      result.push(...childLeafProps);
    }
  });

  return result;
};

export const getAllRowsNodeFromGrid = <T = RowNode>(
  listGridApi: GridApi,
  isLeaf?: boolean,
): T[] => {
  const rowsNode = [];
  if (listGridApi) {
    listGridApi.forEachNodeAfterFilterAndSort((node: RowNode) => {
      if (!isLeaf || get(node, 'data.isLeaf', false)) {
        rowsNode.push(node);
      }
    });
  }

  return rowsNode;
};

export const createTreeNode = (
  name: string,
  baseTreeNode: Partial<ITreeData>,
  additionalTreeFn?: Function,
): ITreeData => {
  const additionalProps = additionalTreeFn
    ? additionalTreeFn(name, baseTreeNode)
    : {};

  return {
    ...baseTreeNode,
    ...additionalProps,
    [INTERNAL_GRID_ROW_ID]: `group_${generateId()}`,
    isLeaf: false,
  };
};

export const sortData = (
  items: IBaseEntityType[],
  path: string,
  secondarySortPath?: string,
): IBaseEntityType[] => {
  const sortFunctions = [
    (item) => toLower(get(item, path, '')),
    secondarySortPath
      ? (item) => toLower(get(item, secondarySortPath, ''))
      : null,
  ].filter(Boolean);

  return orderBy(items, sortFunctions);
};

export const processLeafItems = (
  items: IBaseEntityType[],
  additional: object,
  additionalFn: Function,
): IBaseEntityType[] => {
  return map(items, (item) => {
    return assign(
      {},
      item,
      {
        isLeaf: true,
        ...additional,
      },
      additionalFn ? additionalFn(item) : {},
    );
  });
};

export const getSortedTreeDataByConfig = (
  data: IBaseEntityType[],
  treeConfig: ITreeConverterConfig,
): ITreeData[] => {
  const {
    path = '',
    groupName = '',
    secondarySortPath = '',
    additional,
    additionalFn,
    childrenConfig,
    additionalTreeFn,
    additionalTreeNode,
  } = treeConfig;

  const processedItems = processLeafItems(data, additional, additionalFn);
  const sortedItems = sortData(processedItems, path, secondarySortPath);

  if (!groupName) return sortedItems;

  return chain(sortedItems)
    .groupBy(path)
    .toPairs()
    .map(([name, children]) => {
      const newChildren = childrenConfig
        ? getSortedTreeDataByConfig(children, childrenConfig)
        : children;

      return createTreeNode(
        name,
        {
          [groupName]: name,
          children: newChildren,
          ...additionalTreeNode,
        },
        additionalTreeFn,
      );
    })
    .sortBy((item) => toLower(get(item, groupName, '')))
    .value();
};

export const getNestedProjectPaths = (projectPaths: string[]): string[] => {
  if (isEmpty(projectPaths)) {
    return projectPaths;
  }

  return chain(projectPaths)
    .filter((path) => includes(path, '.'))
    .map((projectPath) => {
      return chain(projectPath)
        .split('.')
        .compact()
        .initial()
        .join('.')
        .value();
    })
    .compact()
    .uniq()
    .value();
};

/**
 * use this to check url is xenial url
 * @param url
 * @returns
 */
export const isXenialUrl = (url: string): boolean => {
  const xenialBaseUrls = [
    environment.xviBackendURI,
    environment.xrptBackendURI,
    environment.xprtBackendURI,
    environment.dmBackendURI,
    environment.auditTrailURI,
  ];

  return some(xenialBaseUrls, (sample) => url.indexOf(sample) === 0);
};

export const getColumnIdWithLabelPath = (
  colId: string,
  labelPaths?: IOrderByUseLabelPath[],
): string => {
  if (isEmpty(labelPaths)) {
    return colId;
  }

  const pathConfig = find(
    labelPaths,
    (labelPath) => colId === get(labelPath, 'path', ''),
  );

  return !isNil(pathConfig) ? get(pathConfig, 'labelPath', colId) : colId;
};

export const isErrorsObject = (item): item is ErrorsObject =>
  has(item, 'errors');

export const toFloatOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = parseFloat(String(value));
  return Number.isNaN(parsed) ? null : parsed;
};

export const buildDecimalPlaceholder = (
  precision: number = DEFAULT_NUMBER_PRECISION,
): string => {
  if (precision <= 0) {
    return '0';
  }

  return `0.${repeat('0', precision)}`;
};

export const buildMaxQuantityValue = (
  integerLength: number,
  precision: number,
  repeatNumber: number = 9,
): number => {
  const normalizedIntegerLength = Math.max(0, Math.trunc(integerLength));
  const normalizedPrecision = Math.max(0, Math.trunc(precision));
  const repeatDigit = `${Math.abs(Math.trunc(repeatNumber)) % 10}`;
  const integerPart =
    normalizedIntegerLength > 0
      ? repeat(repeatDigit, normalizedIntegerLength)
      : '0';

  if (normalizedPrecision <= 0) {
    return parseFloat(integerPart);
  }

  return parseFloat(
    `${integerPart}.${repeat(repeatDigit, normalizedPrecision)}`,
  );
};


/**
 * The method creates a hash-object for some array by a special field path
 * @param array - common array that will be used for creating a new hash-object
 * @param path - an item field path
 */
export const createHashObject = (
  array: IBaseEntityType[] | string[],
  path?: string,
): Record<string, any> => {
  const hashObj: Record<string, any> = {};

  forEach(array, (item) => {
    const valueByPath = get(item, path);

    if (item && typeof item === 'string') {
      hashObj[item] = item;
    } else if (item && path && !isNil(valueByPath)) {
      hashObj[valueByPath] = item;
    }
  });

  return hashObj;
};
